import {
  errorOutcome,
  okOutcome,
  redactMetadata,
  unknownOutcome,
  type CreatePunchoutSessionRequest,
  type CreatePunchoutSessionResponse,
  type PriceCheckLine,
  type PriceCheckRequest,
  type PartsAvailability,
  type PriceCheckResponse,
  type PunchbackEnvelope,
  type PunchbackResult,
  type SubmitSupplierOrderRequest,
  type SubmitSupplierOrderResponse,
  type SupplierAdapter,
  type SupplierCallResult,
  type SupplierCapabilities,
  type SupplierCartLine,
  type SupplierDeliveryInfo,
  type SupplierIntegrationGap,
  type SupplierInvoiceInfo,
  type SupplierOrderStatusResponse,
} from '@forq/parts-data';

/**
 * Der Übungslieferant.
 *
 * Vollständig funktionsfähig, damit der ganze Ablauf -- PunchOut, Warenkorbrückgabe, Preisprüfung,
 * Bestellung, Status, Lieferung, Rechnung -- ohne einen einzigen echten Lieferanten durchgespielt
 * und automatisiert geprüft werden kann.
 *
 * Er ist außerdem der Prüfstein für die Statusmaschine: über vereinbarte Artikelnummern lassen
 * sich die unangenehmen Ausgänge auslösen, die sich bei einem echten Lieferanten nicht bestellen
 * lassen.
 *
 *   PRUEF-TIMEOUT   Zeitüberschreitung beim Absenden -> `unknown` (Bestellung vielleicht draußen)
 *   PRUEF-FEHLER    sauberer Fehlschlag -> `error` (Bestellung nachweislich NICHT draußen)
 *   PRUEF-TEUER     der Preis steigt bei der Prüfung vor dem Absenden um 20 %
 *   PRUEF-WEG       das Teil ist bei der Prüfung nicht mehr im Sortiment
 */

/** Ein Teil im Sortiment des Übungslieferanten. */
interface MockCatalogueEntry {
  readonly supplierItemNumber: string;
  readonly manufacturer: string;
  readonly manufacturerPartNumber: string;
  readonly description: string;
  readonly unit: string;
  readonly unitPrice: number;
  readonly leadTimeDays: number;
}

export const mockCatalogue: readonly MockCatalogueEntry[] = [
  { supplierItemNumber: 'MOCK-10001', manufacturer: 'Linde', manufacturerPartNumber: '0009839123', description: 'Hydraulikfilter', unit: 'Stück', unitPrice: 42.5, leadTimeDays: 1 },
  { supplierItemNumber: 'MOCK-10002', manufacturer: 'Linde', manufacturerPartNumber: '0009831044', description: 'Ölfilter', unit: 'Stück', unitPrice: 18.9, leadTimeDays: 1 },
  { supplierItemNumber: 'MOCK-10003', manufacturer: 'Still', manufacturerPartNumber: '5045210', description: 'Dichtsatz Hubzylinder', unit: 'Satz', unitPrice: 124, leadTimeDays: 3 },
  { supplierItemNumber: 'MOCK-10004', manufacturer: 'Cascade', manufacturerPartNumber: 'GZ-1200', description: 'Gabelzinken 1200 mm (Paar)', unit: 'Paar', unitPrice: 389, leadTimeDays: 7 },
  { supplierItemNumber: 'MOCK-10005', manufacturer: 'Fuchs', manufacturerPartNumber: 'HLP46-20', description: 'Hydrauliköl HLP 46, 20 l', unit: 'Kanister', unitPrice: 76.4, leadTimeDays: 1 },
  { supplierItemNumber: 'MOCK-10006', manufacturer: 'Jungheinrich', manufacturerPartNumber: '51048722', description: 'Bremsbelagsatz', unit: 'Satz', unitPrice: 63.2, leadTimeDays: 2 },
  { supplierItemNumber: 'MOCK-10007', manufacturer: 'Universal', manufacturerPartNumber: 'PUTZ-500', description: 'Putztuchrolle 500 Blatt', unit: 'Rolle', unitPrice: 12.8, leadTimeDays: 1 },
  { supplierItemNumber: 'PRUEF-TIMEOUT', manufacturer: 'Prüfteil', manufacturerPartNumber: 'T-1', description: 'Prüfteil: Zeitüberschreitung beim Absenden', unit: 'Stück', unitPrice: 10, leadTimeDays: 1 },
  { supplierItemNumber: 'PRUEF-FEHLER', manufacturer: 'Prüfteil', manufacturerPartNumber: 'T-2', description: 'Prüfteil: Lieferant lehnt ab', unit: 'Stück', unitPrice: 10, leadTimeDays: 1 },
  { supplierItemNumber: 'PRUEF-TEUER', manufacturer: 'Prüfteil', manufacturerPartNumber: 'T-3', description: 'Prüfteil: Preis steigt vor dem Absenden', unit: 'Stück', unitPrice: 100, leadTimeDays: 2 },
  { supplierItemNumber: 'PRUEF-WEG', manufacturer: 'Prüfteil', manufacturerPartNumber: 'T-4', description: 'Prüfteil: fällt bei der Prüfung weg', unit: 'Stück', unitPrice: 55, leadTimeDays: 2 },
];

const catalogueByNumber = new Map(mockCatalogue.map((entry) => [entry.supplierItemNumber, entry]));

export function findMockCatalogueEntry(supplierItemNumber: string): MockCatalogueEntry | undefined {
  return catalogueByNumber.get(supplierItemNumber.trim().toUpperCase());
}

export function searchMockCatalogue(query: string): readonly MockCatalogueEntry[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return mockCatalogue;
  return mockCatalogue.filter(
    (entry) =>
      entry.supplierItemNumber.toLowerCase().includes(needle) ||
      entry.description.toLowerCase().includes(needle) ||
      entry.manufacturer.toLowerCase().includes(needle) ||
      entry.manufacturerPartNumber.toLowerCase().includes(needle),
  );
}

/** Versandkosten des Übungslieferanten: pauschal, ab 250 € netto frei -- damit die Regel prüfbar ist. */
export function mockShippingCost(netTotal: number): number {
  return netTotal >= 250 ? 0 : 9.9;
}

const capabilities: SupplierCapabilities = {
  punchout: true,
  electronicOrdering: true,
  priceCheck: true,
  orderStatus: true,
  deliveryInfo: true,
  invoiceInfo: true,
  findByCorrelationId: true,
};

/** Der Übungslieferant hat keine Lücken -- er ist ja das Modell, an dem die anderen gemessen werden. */
const gaps: readonly SupplierIntegrationGap[] = [];

interface MockLedgerEntry {
  readonly externalOrderNumber: string;
  readonly correlationId: string;
  readonly acceptedAt: string;
  readonly request: SubmitSupplierOrderRequest;
}

export interface MockSupplierOptions {
  /** Grundadresse des Übungsshops. Vorgabe passt zur Route in FORQ. */
  readonly shopBaseUrl?: string;
  readonly now?: () => Date;
}

/**
 * Der Übungslieferant als Adapter.
 *
 * Hält seine „Bestellungen" in einem eigenen Gedächtnis -- damit `findOrderByCorrelationId` etwas
 * zu finden hat und der unklare Ausgang wirklich aufgeklärt werden kann.
 */
export class MockSupplierAdapter implements SupplierAdapter {
  readonly provider = 'mock' as const;
  readonly capabilities = capabilities;
  readonly gaps = gaps;

  private readonly ledger = new Map<string, MockLedgerEntry>();
  private readonly shopBaseUrl: string;
  private readonly now: () => Date;
  private counter = 0;

  constructor(options: MockSupplierOptions = {}) {
    this.shopBaseUrl = options.shopBaseUrl ?? '/ersatzteile/uebungsshop';
    this.now = options.now ?? (() => new Date());
  }

  async createPunchoutSession(
    request: CreatePunchoutSessionRequest,
  ): Promise<SupplierCallResult<CreatePunchoutSessionResponse>> {
    const supplierSessionId = `mock-sitzung-${++this.counter}`;
    const parameters = new URLSearchParams({
      sitzung: supplierSessionId,
      marke: request.sessionToken,
      rueckgabe: request.returnUrl,
      vorgang: request.workOrderNumber,
    });
    return okOutcome(
      request.correlationId,
      { startUrl: `${this.shopBaseUrl}?${parameters.toString()}`, supplierSessionId },
      // Die Marke steht ABSICHTLICH nicht in den Metadaten -- sie wäre sonst im Protokoll.
      redactMetadata({ supplierSessionId, requestedItems: request.requestedItems.length }),
    );
  }

  async authenticateAndParsePunchback(
    envelope: PunchbackEnvelope,
  ): Promise<SupplierCallResult<PunchbackResult>> {
    const correlationId = envelope.headers['x-forq-correlation'] ?? 'ohne-korrelation';

    if (!envelope.sessionToken) {
      return errorOutcome(correlationId, 'auth', 'Die Rückgabe trägt keinen Sitzungswert.');
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(envelope.rawBody) as unknown;
    } catch {
      return errorOutcome(correlationId, 'contract', 'Die Rückgabe ist kein gültiger Warenkorb.');
    }

    if (!parsed || typeof parsed !== 'object' || !Array.isArray((parsed as { lines?: unknown }).lines)) {
      return errorOutcome(correlationId, 'contract', 'Die Rückgabe enthält keine Positionen.');
    }

    const rawLines = (parsed as { lines: readonly unknown[] }).lines;
    const lines: SupplierCartLine[] = [];

    for (const raw of rawLines) {
      if (!raw || typeof raw !== 'object') continue;
      const entry = raw as Record<string, unknown>;
      const number = String(entry.supplierItemNumber ?? '').trim().toUpperCase();
      const catalogue = catalogueByNumber.get(number);
      if (!catalogue) {
        return errorOutcome(
          correlationId,
          'contract',
          `Der Übungslieferant kennt die Artikelnummer ${number || '(leer)'} nicht.`,
        );
      }
      const quantity = Number(entry.quantity ?? 0);
      lines.push({
        supplierItemNumber: catalogue.supplierItemNumber,
        manufacturer: catalogue.manufacturer,
        manufacturerPartNumber: catalogue.manufacturerPartNumber,
        description: catalogue.description,
        quantity,
        unit: catalogue.unit,
        unitPrice: catalogue.unitPrice,
        currency: 'EUR',
        availability: catalogue.leadTimeDays <= 1 ? 'in-stock' : 'backorder',
        availabilityText: catalogue.leadTimeDays <= 1 ? 'ab Lager' : 'Nachlieferung',
        leadTimeDays: catalogue.leadTimeDays,
        supplierReference: `mock-${catalogue.supplierItemNumber}`,
      });
    }

    if (lines.length === 0) {
      return errorOutcome(correlationId, 'contract', 'Der Warenkorb ist leer.');
    }

    const netTotal = lines.reduce((sum, line) => sum + line.quantity * line.unitPrice, 0);

    return okOutcome(
      correlationId,
      {
        sessionToken: envelope.sessionToken,
        supplierSessionId: String((parsed as { supplierSessionId?: unknown }).supplierSessionId ?? ''),
        currency: 'EUR',
        shippingCost: mockShippingCost(netTotal),
        shippingMethod: 'Paketdienst',
        lines,
      },
      { lineCount: lines.length },
    );
  }

  async checkPrices(request: PriceCheckRequest): Promise<SupplierCallResult<PriceCheckResponse>> {
    const lines = request.items
      .filter((item) => item.supplierItemNumber.toUpperCase() !== 'PRUEF-WEG')
      .map((item): PriceCheckLine => {
        const catalogue = catalogueByNumber.get(item.supplierItemNumber.toUpperCase());
        const base = catalogue?.unitPrice ?? 0;
        const teuer = item.supplierItemNumber.toUpperCase() === 'PRUEF-TEUER';
        const sofort = Boolean(catalogue && catalogue.leadTimeDays <= 1);
        const availability: PartsAvailability = sofort ? 'in-stock' : 'backorder';
        return {
          lineId: item.lineId,
          supplierItemNumber: item.supplierItemNumber,
          unitPrice: teuer ? Math.round(base * 1.2 * 100) / 100 : base,
          currency: 'EUR',
          availability,
          availabilityText: sofort ? 'ab Lager' : 'Nachlieferung',
          leadTimeDays: catalogue?.leadTimeDays ?? null,
        };
      });

    const netTotal = lines.reduce((sum, line) => {
      const item = request.items.find((entry) => entry.lineId === line.lineId);
      return sum + (item?.quantity ?? 0) * line.unitPrice;
    }, 0);

    return okOutcome(
      request.correlationId,
      { lines, shippingCost: mockShippingCost(netTotal), shippingMethod: 'Paketdienst', currency: 'EUR' },
      { checkedLines: lines.length },
    );
  }

  async submitOrder(
    request: SubmitSupplierOrderRequest,
  ): Promise<SupplierCallResult<SubmitSupplierOrderResponse>> {
    const numbers = request.lines.map((line) => line.supplierItemNumber.toUpperCase());

    if (numbers.includes('PRUEF-FEHLER')) {
      return errorOutcome(
        request.correlationId,
        'abgelehnt',
        'Der Übungslieferant lehnt diese Bestellung ab (Prüfteil PRUEF-FEHLER).',
        { purchaseOrderNumber: request.purchaseOrderNumber },
      );
    }

    if (numbers.includes('PRUEF-TIMEOUT')) {
      // Die Bestellung wird trotzdem angenommen -- genau das macht den Fall gefährlich:
      // FORQ weiß es nur nicht. `findOrderByCorrelationId` klärt es später auf.
      this.remember(request);
      return unknownOutcome(
        request.correlationId,
        'timeout',
        'Der Übungslieferant hat nicht rechtzeitig geantwortet. Ob die Bestellung angekommen ist, ist offen.',
        { purchaseOrderNumber: request.purchaseOrderNumber },
      );
    }

    // Idempotenz auch auf Lieferantenseite: derselbe Korrelationswert liefert dieselbe Bestellnummer.
    const existing = this.ledger.get(request.correlationId);
    if (existing) {
      return okOutcome(
        request.correlationId,
        {
          externalOrderNumber: existing.externalOrderNumber,
          acceptedAt: existing.acceptedAt,
          shippingCost: this.shippingFor(existing.request),
          currency: 'EUR',
        },
        { wiederholung: true },
      );
    }

    const entry = this.remember(request);
    return okOutcome(
      request.correlationId,
      {
        externalOrderNumber: entry.externalOrderNumber,
        acceptedAt: entry.acceptedAt,
        shippingCost: this.shippingFor(request),
        currency: 'EUR',
      },
      { purchaseOrderNumber: request.purchaseOrderNumber },
    );
  }

  async fetchOrderStatus(
    externalOrderNumber: string,
    correlationId: string,
  ): Promise<SupplierCallResult<SupplierOrderStatusResponse>> {
    const entry = this.byExternalNumber(externalOrderNumber);
    if (!entry) {
      return errorOutcome(correlationId, 'not-found', 'Der Übungslieferant kennt diese Bestellnummer nicht.');
    }
    return okOutcome(correlationId, {
      externalOrderNumber,
      state: 'confirmed',
      stateText: 'Bestätigt',
      lines: entry.request.lines.map((line) => ({
        supplierItemNumber: line.supplierItemNumber,
        orderedQuantity: line.quantity,
        shippedQuantity: 0,
        expectedAt: null,
      })),
    });
  }

  async fetchOrderConfirmation(
    externalOrderNumber: string,
    correlationId: string,
  ): Promise<SupplierCallResult<SupplierOrderStatusResponse>> {
    return this.fetchOrderStatus(externalOrderNumber, correlationId);
  }

  async fetchDeliveryInfo(
    externalOrderNumber: string,
    correlationId: string,
  ): Promise<SupplierCallResult<SupplierDeliveryInfo>> {
    const entry = this.byExternalNumber(externalOrderNumber);
    if (!entry) {
      return errorOutcome(correlationId, 'not-found', 'Der Übungslieferant kennt diese Bestellnummer nicht.');
    }
    return okOutcome(correlationId, {
      externalOrderNumber,
      deliveryNoteNumber: `${externalOrderNumber}-LS`,
      carrier: 'Übungsspedition',
      trackingReference: `UEB${externalOrderNumber.replace(/\D/g, '')}`,
      shippedAt: entry.acceptedAt,
    });
  }

  async fetchInvoiceInfo(
    externalOrderNumber: string,
    correlationId: string,
  ): Promise<SupplierCallResult<SupplierInvoiceInfo>> {
    const entry = this.byExternalNumber(externalOrderNumber);
    if (!entry) {
      return errorOutcome(correlationId, 'not-found', 'Der Übungslieferant kennt diese Bestellnummer nicht.');
    }
    const net =
      entry.request.lines.reduce((sum, line) => sum + line.quantity * line.unitPrice, 0) +
      this.shippingFor(entry.request);
    return okOutcome(correlationId, {
      externalOrderNumber,
      invoiceNumber: `${externalOrderNumber}-RE`,
      invoiceDate: entry.acceptedAt.slice(0, 10),
      netAmount: Math.round(net * 100) / 100,
      grossAmount: Math.round(net * 1.19 * 100) / 100,
      currency: 'EUR',
    });
  }

  async findOrderByCorrelationId(
    correlationId: string,
  ): Promise<SupplierCallResult<SubmitSupplierOrderResponse>> {
    const entry = this.ledger.get(correlationId);
    if (!entry) {
      return errorOutcome(
        correlationId,
        'not-found',
        'Zu diesem Vorgang liegt beim Übungslieferanten keine Bestellung vor.',
      );
    }
    return okOutcome(correlationId, {
      externalOrderNumber: entry.externalOrderNumber,
      acceptedAt: entry.acceptedAt,
      shippingCost: this.shippingFor(entry.request),
      currency: 'EUR',
    });
  }

  private remember(request: SubmitSupplierOrderRequest): MockLedgerEntry {
    const entry: MockLedgerEntry = {
      externalOrderNumber: `MOCK-${String(++this.counter).padStart(6, '0')}`,
      correlationId: request.correlationId,
      acceptedAt: this.now().toISOString(),
      request,
    };
    this.ledger.set(request.correlationId, entry);
    return entry;
  }

  private byExternalNumber(externalOrderNumber: string): MockLedgerEntry | undefined {
    return [...this.ledger.values()].find(
      (entry) => entry.externalOrderNumber === externalOrderNumber,
    );
  }

  private shippingFor(request: SubmitSupplierOrderRequest): number {
    return mockShippingCost(request.lines.reduce((sum, line) => sum + line.quantity * line.unitPrice, 0));
  }
}

export function createMockSupplierAdapter(options: MockSupplierOptions = {}): MockSupplierAdapter {
  return new MockSupplierAdapter(options);
}
